// Updated BasicInformationForm - REMOVED duplicate API call
import React, { useState } from 'react';
import {
  Box,
  Grid,
  TextField,
  MenuItem,
  Typography,
  Skeleton,
  Alert,
  Divider
} from '@mui/material';
import AudioDescriptionField from './AudioDescriptionField';
import UserRoleSelector from './UserRoleSelector';
import { BUSINESS_TYPES, TOP_CATEGORIES, DESCRIPTION_PROMPTS, DESCRIPTION_PROMPT_DEFAULT } from './BusinessFormConstants';

const BasicInformationForm = ({ 
  formData, 
  onChange, 
  isLoading, 
  errors = {}, 
  setErrors,
  // 🆕 RECEIVE AUDIO DATA AS PROPS FROM PARENT
  audioPresignedData,
  isAudioUrlLoading,
  audioUrlError 
}) => {
  const [audioProcessing, setAudioProcessing] = useState(false);

  // 🆕 REMOVED DUPLICATE useGetPreSignedUrlAudioQuery - now comes from props

  const validateField = (name, value) => {
    if (name === 'businessType' && !value) {
      return 'Business type is required';
    }
    if (name === 'businessName' && !value?.trim()) {
      return 'Business name is required';
    }
    if (name === 'category' && !value) {
      return 'Category is required';
    }
    if (name === 'phone' && !value?.trim()) {
      return 'Phone number is required';
    }
    if (name === 'email' && !value?.trim()) {
      return 'Email is required';
    }
    return '';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Clear error when user types
    setErrors(prev => ({
      ...prev,
      [name]: validateField(name, value)
    }));
    onChange(e);
  };

  // Handle audio processing state
  const handleAudioProcessing = async (audioInfo) => {
    try {
      setAudioProcessing(true);
      console.log('Audio processed:', audioInfo);
      // Note: refetchAudioUrl would need to be passed from parent if needed
    } catch (error) {
      console.error('Error processing audio:', error);
    } finally {
      setAudioProcessing(false);
    }
  };

  if (isLoading) {
    return (
      <Box>
        <Skeleton variant="text" width="40%" height={32} sx={{ mb: 2 }} />
        <Grid container spacing={3}>
          {[1, 2, 3, 4, 5, 6, 7, 8].map((item) => (
            <Grid item xs={12} key={item}>
              <Skeleton variant="rectangular" height={56} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Basic Information
      </Typography>
      
      <Grid container spacing={3}>
        {/* User Role */}
        <Grid item xs={12}>
          <UserRoleSelector
            value={formData.userRole}
            onChange={(e) => handleChange(e)}
            error={errors.userRole}
          />
        </Grid>

        {/* Business Type */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            select
            label="Business Type"
            name="businessType"
            value={formData.businessType || ''}
            onChange={handleChange}
            required
            error={!!errors.businessType}
            helperText={errors.businessType || 'Select whether your business has a physical location or is online-only'}
          >
            {BUSINESS_TYPES.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* Business Name */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Business Name"
            name="businessName"
            value={formData.businessName}
            onChange={handleChange}
            required
            error={!!errors.businessName}
            helperText={errors.businessName}
          />
        </Grid>

        {/* Primary Category */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            select
            label="Primary Category"
            name="category"
            value={formData.category}
            onChange={handleChange}
            required
            error={!!errors.category}
            helperText={errors.category}
          >
            {TOP_CATEGORIES.map((category) => (
              <MenuItem key={category.id} value={category.name}>
                {category.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* Contact Information Section */}
        <Grid item xs={12}>
          <Divider sx={{ my: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Contact Information
            </Typography>
          </Divider>
        </Grid>

        {/* Phone */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Phone Number"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            required
            error={!!errors.phone}
            helperText={errors.phone}
          />
        </Grid>

        {/* Email */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            required
            error={!!errors.email}
            helperText={errors.email}
          />
        </Grid>

        {/* Website */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Website"
            name="website"
            value={formData.website}
            onChange={onChange}
            helperText="Optional - Include your business website URL"
          />
        </Grid>

        {/* Business Description Section */}
        <Grid item xs={12}>
          <Divider sx={{ my: 2 }}>
            <Typography variant="body2" color="text.secondary">
              What you offer
            </Typography>
          </Divider>
        </Grid>

        {/* Text Description */}
        <Grid item xs={12}>
          {/* Guided, not blank: the quality check needs NAMED services, and an
              open "describe your business" box got empty fields or requests
              ("can you make a poster") — Oct 2026 cohort. */}
          <TextField
            fullWidth
            multiline
            rows={3}
            required={!formData.audioDescription?.file}
            label={(DESCRIPTION_PROMPTS[formData.category] || DESCRIPTION_PROMPT_DEFAULT).label}
            name="description"
            value={formData.description}
            onChange={handleChange}
            error={!!errors.description}
            placeholder={(DESCRIPTION_PROMPTS[formData.category] || DESCRIPTION_PROMPT_DEFAULT).placeholder}
            helperText={errors.description
              || 'Revfer only writes about what you put here. Name your products, services and specialties — the more specific you are, the more varied your posts will be. Any language is fine.'}
          />
        </Grid>

        {/* Audio Description */}
        <Grid item xs={12}>
          <AudioDescriptionField
            formData={formData}
            onChange={onChange}
            isLoading={audioProcessing || isAudioUrlLoading}
            onAudioProcessing={handleAudioProcessing}
          />
          
          {/* Show audio upload errors */}
          {audioUrlError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              Audio upload error: {audioUrlError.message || 'Failed to prepare audio upload'}
            </Alert>
          )}
          
          {/* Show audio processing status */}
          {formData.audioDescription && audioPresignedData && (
            <Alert severity="success" sx={{ mt: 1 }}>
              Audio ready for upload: {audioPresignedData.keyPath}
            </Alert>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default BasicInformationForm;

// Export helper function for audio upload (used in main form submission)
export const uploadAudioToS3 = async (audioDescription, audioPresignedData, userId) => {
  if (!audioDescription?.file || !audioPresignedData?.url) {
    return null;
  }

  try {
    const response = await fetch(audioPresignedData.url, {
      method: 'PUT',
      body: audioDescription.file,
      headers: {
        'Content-Type': `audio/${audioDescription.type}`,
        'x-amz-meta-eventtype': 'BusinessCreateAudio',
        'x-amz-meta-audiotype': audioDescription.type,
        
      },
    });

    if (!response.ok) {
      throw new Error('Failed to upload audio');
    }

    return audioPresignedData.keyPath;
  } catch (error) {
    console.error('Error uploading audio:', error);
    throw error;
  }
};